import { z } from 'zod';
import { ReferralChannelEnum } from '../startParam.js';

/**
 * Схемы QuestionBox для API контракта (Веха 3).
 *
 * Источник правды:
 *   - CANON §2.B (анонимность): publicView НЕ содержит ownerId / username / firstName.
 *   - CANON §2.D (zod-strict): все .strict() — unknown поля отвергаются.
 *   - threat-models/api.md → I «список боксов через GET /:token».
 *
 * BOX_FORMATS — список форматов, MVP активно использует только 'ask'. Остальные
 * подключатся в Вехе 8 (K-фактор / новые форматы). enum зафиксирован сейчас,
 * чтобы будущие body не сломали валидацию.
 *
 * 'message' (Трек D) — «напиши мне анонимно»: отдельный ящик под свободные
 * анонимные сообщения (не вопрос-ответ по умолчанию), использует тот же
 * Question-механизм хранения/модерации, что и остальные форматы.
 */
export const BOX_FORMATS = ['ask', 'rate', 'confession', 'guesswho', 'message'] as const;
export const BoxFormatEnum = z.enum(BOX_FORMATS);
export type BoxFormat = z.infer<typeof BoxFormatEnum>;

// boxToken — публичный токен бокса (зеркало questions.ts: min 8 / max 64). Тот же
// диапазон, что и `CreateQuestionBody.boxToken` / `RecordEventBody.boxToken`.
const refBoxTokenSchema = z.string().min(8).max(64);

/**
 * Referral-вход (Веха 8 M2, контроль #6/#7/#8, threat-model S.c/E.e).
 * `sourceBoxToken` — публичный токен чужого бокса из deep-link (adversarial-
 * controlled): резолвится серверно через `findActiveBoxByToken`, нет/inactive →
 * атрибуция тихо пропускается (fail-soft). `channel` — из префикса start_param.
 */
export const BoxRefInput = z
  .object({
    sourceBoxToken: refBoxTokenSchema,
    channel: ReferralChannelEnum,
  })
  .strict();
export type BoxRefInput = z.infer<typeof BoxRefInput>;

/**
 * Тело `POST /boxes`. Идемпотентно: если у юзера есть активный бокс с тем
 * же `format`, сервер возвращает существующий (CANON Q5/S5). `prompt` —
 * опциональный кастомный текст, max 200 (UI-комфорт + storage).
 *
 * `ref` (Веха 8 M2) — опциональная Referral-атрибуция: откуда пришёл новый юзер
 * (deep-link чужого бокса + канал). Атрибуция выполняется ТОЛЬКО на первом боксе
 * нового юзера; не валит ответ при сбое (fail-soft).
 */
export const CreateBoxBody = z
  .object({
    format: BoxFormatEnum.default('ask'),
    prompt: z.string().max(200).optional(),
    ref: BoxRefInput.optional(),
  })
  .strict();
export type CreateBoxBody = z.infer<typeof CreateBoxBody>;

/**
 * Public view бокса для `GET /boxes/:token` (без auth) — анонимный отправитель
 * видит ТОЛЬКО format/prompt/isActive. НЕТ ownerId, username, firstName, deepLink
 * (CANON B2 + threat-models/api.md → I).
 */
export const BoxPublicView = z
  .object({
    token: z.string(),
    format: BoxFormatEnum,
    prompt: z.string().nullable(),
    isActive: z.boolean(),
  })
  .strict();
export type BoxPublicView = z.infer<typeof BoxPublicView>;

/**
 * Owner view бокса для `POST /boxes` и вложенного `box` в `GET /inbox`.
 * Содержит deepLink для шеринга — но deepLink может быть null если в env
 * не задан `BOT_USERNAME` / `MINIAPP_SHORT_NAME` (graceful до Вехи 4).
 *
 * `inviteCardUrl` (Веха 5):
 *   - На `POST /boxes` — рендерится через Satori + S3 upload (lazy + idempotent
 *     HEAD-cache, CANON F3); валидный публичный CDN URL.
 *   - На `GET /inbox` — всегда `null` (рендер не блокирует список;
 *     mini-app дёргает POST /boxes для свежего URL).
 *   - На render-failure — `null` (fail-soft, CANON F4).
 *
 * createdAt сериализуется как ISO-8601 (z.string().datetime()).
 */
export const BoxOwnerView = z
  .object({
    id: z.string(),
    token: z.string(),
    format: BoxFormatEnum,
    prompt: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
    deepLink: z.string().url().nullable(),
    /**
     * Story-tagged deep-link (`boxs_<token>`, Веха 8 M2) — для `widgetLink` в
     * shareStory (атрибуция канала 'story'). `deepLink` остаётся direct
     * (`box_`, copy-link). null по тем же причинам, что и deepLink (env).
     */
    storyShareUrl: z.string().url().nullable(),
    inviteCardUrl: z.string().url().nullable(),
  })
  .strict();
export type BoxOwnerView = z.infer<typeof BoxOwnerView>;

/**
 * Резолв бокса по коду (work-019, CANON B2).
 *
 * `GET /boxes/by-code/:code` → отдаёт ТОЛЬКО format/prompt/isActive.
 * НЕТ token/ownerId/username — резолв по коду не должен конвертироваться
 * в вечный deep-link (ротация кода = гашение канала, треб. 6).
 * Отдельный mapper на стороне API — НЕ mapToPublicView (тот отдаёт token).
 */
export const BoxByCodeView = z
  .object({
    format: BoxFormatEnum,
    prompt: z.string().nullable(),
    isActive: z.boolean(),
  })
  .strict();
export type BoxByCodeView = z.infer<typeof BoxByCodeView>;

/**
 * Ответ выдачи/ротации кода (work-019 + work-021).
 *
 * `POST /boxes/code` / `POST /boxes/code/rotate` → `{ code, inviteCardUrl }`.
 *
 * `inviteCardUrl` (work-021): URL invite-карточки с кодом на ней (S3/CDN).
 * null при render-failure (fail-soft, CANON F4) — модалка покажет degraded share.
 */
export const BoxCodeView = z
  .object({
    code: z.string(),
    inviteCardUrl: z.string().url().nullable(),
  })
  .strict();
export type BoxCodeView = z.infer<typeof BoxCodeView>;
